import { BULK_ALIAS_BATCH_SIZE } from '@/api/services/BankService'
import { ITransaction } from '@/types'

export interface AliasBatch {
  accountId: string
  transactionIds: string[]
}

// The endpoint takes one account and at most BULK_ALIAS_BATCH_SIZE transactions a request, so the
// selection is grouped by account and each group split into batches. Accounts keep the order they
// first appear in, so the batches follow the rows as reviewed.
export const planAliasBatches = (
  rows: ITransaction[],
  batchSize = BULK_ALIAS_BATCH_SIZE
): AliasBatch[] => {
  const byAccount = new Map<string, string[]>()
  for (const row of rows) {
    const ids = byAccount.get(row.accountId) ?? []
    // A transaction may appear only once in a request.
    if (!ids.includes(row.transactionId)) {
      ids.push(row.transactionId)
    }
    byAccount.set(row.accountId, ids)
  }

  const batches: AliasBatch[] = []
  byAccount.forEach((ids, accountId) => {
    for (let start = 0; start < ids.length; start += batchSize) {
      batches.push({ accountId, transactionIds: ids.slice(start, start + batchSize) })
    }
  })
  return batches
}

export interface AliasRunResult {
  updated: string[]
  // Set when a batch failed. Nothing after it was sent, so everything from it on is still to do --
  // though the failed batch itself may have been partly applied.
  failure?: { error: unknown; remaining: string[] }
}

// Sends the batches one at a time and stops at the first that fails. A failed batch may have
// been partly applied, but the backend cannot say how far it got, so its rows are all treated as
// not done -- setting an alias is idempotent, so sending them again is safe.
export const runAliasBatches = async (
  batches: AliasBatch[],
  send: (batch: AliasBatch) => Promise<void>,
  onBatchDone?: (updated: string[]) => void
): Promise<AliasRunResult> => {
  const updated: string[] = []

  for (let index = 0; index < batches.length; index++) {
    try {
      await send(batches[index])
    } catch (error) {
      const remaining = batches.slice(index).flatMap(batch => batch.transactionIds)
      return { updated, failure: { error, remaining } }
    }
    updated.push(...batches[index].transactionIds)
    onBatchDone?.(batches[index].transactionIds)
  }

  return { updated }
}

export const errorMessage = (error: unknown) => {
  const err = error as { response?: { data?: { message?: string } }; message?: string }
  return err?.response?.data?.message ?? err?.message ?? 'Something went wrong.'
}
